import React, { useEffect, useMemo, useState } from 'react';
import axios from '../axios';
import { useToast } from '../components/ToastProvider';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import StatusBadge from '../components/StatusBadge';
import MobileDataCard from '../components/MobileDataCard';
import { formatCurrency, formatDateTime } from '../utils/formatters';

interface Deal {
  id: string;
  companyName: string;
  status: string;
  dealType?: 'NEW_BUSINESS' | 'RENEWAL' | 'UPSELL' | 'REACTIVATION' | 'ORGANIZATIONAL';
  originatedByCloser?: boolean;
  revenueAmount?: number;
  commissionAmount?: number;
  setterCommissionAmount?: number;
  closerCommissionAmount?: number;
  closerCommissionTier?: 'TEAM_MEMBER' | 'EXTERNAL' | null;
  setterEmail?: string;
  closerEmail?: string;
}

interface DealDetail {
  id: string;
  companyName: string;
  companyStatus: string;
  status: string;
  dealType?: 'NEW_BUSINESS' | 'RENEWAL' | 'UPSELL' | 'REACTIVATION' | 'ORGANIZATIONAL';
  originatedByCloser?: boolean;
  revenueAmount?: number;
  commissionAmount?: number;
  setterCommissionAmount?: number;
  closerCommissionAmount?: number;
  closerCommissionTier?: 'TEAM_MEMBER' | 'EXTERNAL' | null;
  closedAt?: string;
  setterEmail?: string;
  closerEmail?: string;
  leadId?: string;
  leadStatus?: string;
  partnerEmail?: string;
  partnerPhoneNumber?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  salesTip?: string;
  closerComment?: string;
  createdAt: string;
}

const ACTIVE_DEAL_STATUSES = ['OPEN', 'QUALIFIED', 'OUTREACH_STARTED', 'MEETING_SCHEDULED', 'PROPOSAL_SENT', 'IN_NEGOTIATION'] as const;
const NEXT_STATUS_OPTIONS: Record<string, string[]> = {
  OPEN: ['QUALIFIED', 'CLOSED_LOST'],
  QUALIFIED: ['OUTREACH_STARTED', 'CLOSED_LOST'],
  OUTREACH_STARTED: ['MEETING_SCHEDULED', 'CLOSED_LOST'],
  MEETING_SCHEDULED: ['PROPOSAL_SENT', 'CLOSED_LOST'],
  PROPOSAL_SENT: ['IN_NEGOTIATION', 'CLOSED_LOST'],
  IN_NEGOTIATION: ['PROPOSAL_SENT', 'CLOSED_LOST'],
};

const getSelectableStatusOptions = (currentStatus: string) => {
  const nextOptions = NEXT_STATUS_OPTIONS[currentStatus] || [];
  return [currentStatus, ...nextOptions.filter((status) => status !== currentStatus)];
};

const DealManagement: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [query, setQuery] = useState('');
  const [detailDeal, setDetailDeal] = useState<DealDetail | null>(null);
  const [actionDealTarget, setActionDealTarget] = useState<Deal | null>(null);
  const [closeDealTarget, setCloseDealTarget] = useState<Deal | null>(null);
  const [revenue, setRevenue] = useState('');
  const [dealType, setDealType] = useState<'NEW_BUSINESS' | 'RENEWAL' | 'UPSELL' | 'REACTIVATION' | 'ORGANIZATIONAL'>('NEW_BUSINESS');
  const [setterMode, setSetterMode] = useState<'EXISTING' | 'CLOSER' | 'NONE'>('EXISTING');
  const [originatedByCloser, setOriginatedByCloser] = useState(false);
  const [statusSelections, setStatusSelections] = useState<Record<string, string>>({});
  const [statusBusy, setStatusBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const toast = useToast();
  const locale = i18n.resolvedLanguage;

  const formatAmount = (value?: number) => (value == null ? '-' : formatCurrency(value, locale));
  const getCloserTierLabel = (tier?: 'TEAM_MEMBER' | 'EXTERNAL' | null) => (tier ? t(`closerType.${tier}`) : '-');
  const getDealTypeLabel = (value?: Deal['dealType']) => (value ? t(`dealType.${value}`) : '-');
  const canCloseDeal = (status: string) => status !== 'CLOSED_WON';
  const canManageDeal = (status: string) => Boolean(NEXT_STATUS_OPTIONS[status]?.length) || canCloseDeal(status);

  const fetchDeals = async () => {
    const response = await axios.get('/api/admin/deals');
    setDeals(response.data);
    setStatusSelections(
      Object.fromEntries(
        (response.data || [])
          .filter((deal: Deal) => NEXT_STATUS_OPTIONS[deal.status]?.length)
          .map((deal: Deal) => [deal.id, deal.status])
      )
    );
  };

  useEffect(() => {
    fetchDeals();
  }, []);

  const openDealDetail = async (dealId: string) => {
    try {
      setDetailLoading(true);
      const res = await axios.get(`/api/admin/deals/${dealId}`);
      setDetailDeal(res.data);
    } catch (e: any) {
      toast.show(String(e?.response?.data || t('deals.loadError')), 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  const updateDealStatus = async (dealId: string) => {
    const status = statusSelections[dealId];
    const currentDeal = deals.find((deal) => deal.id === dealId);
    if (!status || !currentDeal || status === currentDeal.status) return;
    try {
      setStatusBusy(`${dealId}:${status}`);
      await axios.patch(`/api/admin/deals/${dealId}/status`, { status });
      toast.show(t('deals.statusUpdateSuccess') as string, 'success');
      setActionDealTarget(null);
      if (detailDeal?.id === dealId) {
        setDetailDeal(null);
      }
      fetchDeals();
    } catch (e: any) {
      toast.show(String(e?.response?.data || t('deals.statusUpdateError')), 'error');
    } finally {
      setStatusBusy(null);
    }
  };

  const closeDeal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!closeDealTarget) return;
    try {
      setLoading(true);
      await axios.post(`/api/admin/deals/${closeDealTarget.id}/close`, {
        revenueAmount: Number(revenue),
        dealType,
        originatedByCloser,
        setterId: setterMode === 'CLOSER' ? user?.id : null,
        clearSetter: setterMode === 'NONE',
      });
      toast.show(t('deals.closeSuccess') as string, 'success');
      setCloseDealTarget(null);
      setRevenue('');
      setDealType('NEW_BUSINESS');
      setSetterMode('EXISTING');
      setOriginatedByCloser(false);
      fetchDeals();
      if (detailDeal && detailDeal.id === closeDealTarget.id) {
        setDetailDeal(null);
      }
    } catch (err: any) {
      toast.show(String(err?.response?.data || t('deals.closeError')), 'error');
    } finally {
      setLoading(false);
    }
  };

  const filteredDeals = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return deals;
    return deals.filter((d) =>
      d.companyName.toLowerCase().includes(q) ||
      t(`status.deal.${d.status}`, d.status).toLowerCase().includes(q) ||
      (d.setterEmail || '').toLowerCase().includes(q) ||
      (d.closerEmail || '').toLowerCase().includes(q)
    );
  }, [deals, query, t]);

  const openCloseDealModal = (deal: Deal) => {
    setCloseDealTarget(deal);
    setRevenue('');
    setDealType(deal.dealType || 'NEW_BUSINESS');
    setSetterMode(deal.setterEmail === user?.email ? 'CLOSER' : (deal.setterEmail ? 'EXISTING' : 'NONE'));
    setOriginatedByCloser(Boolean(deal.originatedByCloser) || deal.setterEmail === user?.email);
  };

  return (
    <section className="mx-auto">
      <h1 className="text-2xl md:text-3xl font-bold mb-6 text-[#111D4E]">{t('deals.managementTitle')}</h1>

      <div className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden mb-6">
        <div className="px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
          <div className="text-sm font-semibold text-[#111D4E]">{t('deals.searchTitle')}</div>
          <div className="text-xs text-gray-600">{t('deals.searchSubtitle')}</div>
        </div>
        <div className="p-5">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('deals.searchPlaceholder')}
            className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white"
          />
        </div>
      </div>

      <div className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
          <div>
            <div className="text-sm font-semibold text-[#111D4E]">{t('deals.title')}</div>
            <div className="text-xs text-gray-600">{t('deals.subtitle')}</div>
          </div>
          <button onClick={fetchDeals} className="px-4 py-2 bg-[#47ABDB] text-white rounded-md text-sm font-semibold shadow-sm hover:bg-[#3d8fb8]">{t('common.refresh')}</button>
        </div>
        <div className="space-y-3 p-4 md:hidden">
          {filteredDeals.length === 0 ? (
            <div className="rounded-2xl border border-slate-200/80 bg-white px-5 py-10 text-center text-base text-gray-500">{t('deals.empty')}</div>
          ) : (
            filteredDeals.map((deal) => (
              <MobileDataCard
                key={deal.id}
                title={deal.companyName}
                badge={<StatusBadge kind="deal" status={deal.status} />}
                rows={[
                  { label: t('deals.setter'), value: deal.setterEmail || '-' },
                  { label: t('deals.closer'), value: deal.closerEmail || '-' },
                  { label: t('deals.dealType'), value: getDealTypeLabel(deal.dealType) },
                  { label: t('deals.revenue'), value: formatAmount(deal.revenueAmount) },
                  { label: t('deals.commission'), value: formatAmount(deal.commissionAmount) },
                ]}
                actions={
                  <>
                    <button
                      onClick={() => openDealDetail(deal.id)}
                      disabled={detailLoading}
                      className="w-full px-4 py-2 bg-slate-200 text-slate-700 rounded-md text-sm font-semibold disabled:opacity-60"
                    >
                      {t('deals.viewDetails')}
                    </button>
                    {canManageDeal(deal.status) && (
                      <button
                        onClick={() => setActionDealTarget(deal)}
                        className="w-full px-4 py-2 bg-[#47ABDB] text-white rounded-md text-sm font-semibold"
                      >
                        {t('deals.manageDeal')}
                      </button>
                    )}
                  </>
                }
              />
            ))
          )}
        </div>
        <div className="hidden overflow-x-auto md:block">
          <table className="min-w-full text-left table-auto text-[15px] md:text-base">
            <thead className="bg-[#F3F6F9] text-sm uppercase tracking-wide text-[#111D4E]">
              <tr>
                <th className="px-5 py-3 font-semibold">{t('deals.company')}</th>
                <th className="px-5 py-3 font-semibold">{t('deals.setterOpenedBy')}</th>
                <th className="px-5 py-3 font-semibold">{t('deals.closer')}</th>
                <th className="px-5 py-3 font-semibold">{t('deals.status')}</th>
                <th className="px-5 py-3 font-semibold">{t('deals.dealType')}</th>
                <th className="px-5 py-3 font-semibold">{t('deals.revenue')}</th>
                <th className="px-5 py-3 font-semibold">{t('deals.commission')}</th>
                <th className="px-5 py-3 font-semibold">{t('deals.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredDeals.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-5 py-10 text-center text-base text-gray-500">{t('deals.empty')}</td>
                </tr>
              ) : (
                filteredDeals.map((deal, idx) => (
                  <tr key={deal.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FBFCFE]'}>
                    <td className="px-5 py-4 font-medium text-[#111D4E]">{deal.companyName}</td>
                    <td className="px-5 py-4 text-sm">{deal.setterEmail || '-'}</td>
                    <td className="px-5 py-4 text-sm">{deal.closerEmail || '-'}</td>
                    <td className="px-5 py-4">
                      <StatusBadge kind="deal" status={deal.status} />
                    </td>
                    <td className="px-5 py-4 text-sm">{getDealTypeLabel(deal.dealType)}</td>
                    <td className="px-5 py-4">{formatAmount(deal.revenueAmount)}</td>
                    <td className="px-5 py-4">{formatAmount(deal.commissionAmount)}</td>
                    <td className="px-5 py-4 space-x-2">
                      <button
                        onClick={() => openDealDetail(deal.id)}
                        disabled={detailLoading}
                        className="px-3 py-1 bg-slate-200 text-slate-700 rounded text-sm disabled:opacity-60"
                      >
                        {t('deals.viewDetails')}
                      </button>
                      {canManageDeal(deal.status) && (
                        <button
                          onClick={() => setActionDealTarget(deal)}
                          className="px-3 py-1 bg-[#47ABDB] text-white rounded text-sm"
                        >
                          {t('deals.manageDeal')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {detailDeal && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center px-4 py-4">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white text-gray-800 rounded-2xl shadow-sm border border-slate-200/80">
            <div className="px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
              <div className="text-sm font-semibold text-[#111D4E]">{t('deals.detailsTitle')}</div>
              <div className="text-xs text-gray-600">{t('deals.detailsSubtitle')}</div>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div><span className="font-semibold">{t('deals.company')}:</span> {detailDeal.companyName}</div>
              <div><span className="font-semibold">{t('deals.companyStatus')}:</span> <StatusBadge kind="company" status={detailDeal.companyStatus} /></div>
              <div><span className="font-semibold">{t('deals.status')}:</span> <StatusBadge kind="deal" status={detailDeal.status} /></div>
              <div><span className="font-semibold">{t('deals.dealType')}:</span> {getDealTypeLabel(detailDeal.dealType)}</div>
              <div><span className="font-semibold">{t('deals.createdAt')}:</span> {formatDateTime(detailDeal.createdAt, locale)}</div>
              <div><span className="font-semibold">{t('deals.setter')}:</span> {detailDeal.setterEmail || '-'}</div>
              <div><span className="font-semibold">{t('deals.closer')}:</span> {detailDeal.closerEmail || '-'}</div>
              <div><span className="font-semibold">{t('deals.closerTier')}:</span> {getCloserTierLabel(detailDeal.closerCommissionTier)}</div>
              <div><span className="font-semibold">{t('deals.originatedByCloser')}:</span> {detailDeal.originatedByCloser ? t('common.yes') : t('common.no')}</div>
              <div><span className="font-semibold">{t('deals.revenue')}:</span> {formatAmount(detailDeal.revenueAmount)}</div>
              <div><span className="font-semibold">{t('deals.commission')}:</span> {formatAmount(detailDeal.commissionAmount)}</div>
              <div><span className="font-semibold">{t('deals.setterCommission')}:</span> {formatAmount(detailDeal.setterCommissionAmount)}</div>
              <div><span className="font-semibold">{t('deals.closerCommission')}:</span> {formatAmount(detailDeal.closerCommissionAmount)}</div>
              <div><span className="font-semibold">{t('deals.closedAt')}:</span> {detailDeal.closedAt ? formatDateTime(detailDeal.closedAt, locale) : '-'}</div>
              <div><span className="font-semibold">{t('deals.leadStatus')}:</span> {detailDeal.leadStatus ? <StatusBadge kind="lead" status={detailDeal.leadStatus} /> : '-'}</div>
              <div><span className="font-semibold">{t('deals.partner')}:</span> {detailDeal.partnerEmail || '-'}</div>
              <div><span className="font-semibold">{t('deals.setterPhone')}:</span> {detailDeal.partnerPhoneNumber || '-'}</div>
              <div><span className="font-semibold">{t('submitLead.contactName')}:</span> {detailDeal.contactName || '-'}</div>
              <div><span className="font-semibold">{t('submitLead.contactEmail')}:</span> {detailDeal.contactEmail || '-'}</div>
              <div><span className="font-semibold">{t('submitLead.contactPhone')}:</span> {detailDeal.contactPhone || '-'}</div>
              <div className="md:col-span-2"><span className="font-semibold">{t('submitLead.salesTip')}:</span> {detailDeal.salesTip || '-'}</div>
              <div className="md:col-span-2"><span className="font-semibold">{t('admin.leads.closerComment')}:</span> {detailDeal.closerComment || '-'}</div>
            </div>
            <div className="px-6 pb-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setDetailDeal(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md"
              >
                {t('common.close')}
              </button>
              {canCloseDeal(detailDeal.status) && (
                <button
                  onClick={() => {
                    setCloseDealTarget({
                      id: detailDeal.id,
                      companyName: detailDeal.companyName,
                      status: detailDeal.status,
                      dealType: detailDeal.dealType,
                      originatedByCloser: detailDeal.originatedByCloser,
                      revenueAmount: detailDeal.revenueAmount,
                      commissionAmount: detailDeal.commissionAmount,
                      setterCommissionAmount: detailDeal.setterCommissionAmount,
                      closerCommissionAmount: detailDeal.closerCommissionAmount,
                      closerCommissionTier: detailDeal.closerCommissionTier,
                      setterEmail: detailDeal.setterEmail,
                      closerEmail: detailDeal.closerEmail,
                    });
                    setDetailDeal(null);
                    setRevenue('');
                    setDealType(detailDeal.dealType || 'NEW_BUSINESS');
                    setSetterMode(detailDeal.setterEmail === user?.email ? 'CLOSER' : (detailDeal.setterEmail ? 'EXISTING' : 'NONE'));
                    setOriginatedByCloser(Boolean(detailDeal.originatedByCloser));
                  }}
                  className="px-4 py-2 bg-[#47ABDB] text-white rounded-md"
                >
                  {t('deals.closeThisDeal')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {actionDealTarget && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center px-4 py-4">
          <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white text-gray-800 rounded-2xl shadow-sm border border-slate-200/80">
            <div className="px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
              <div className="text-sm font-semibold text-[#111D4E]">{t('deals.manageDealTitle')}</div>
              <div className="text-xs text-gray-600">{actionDealTarget.companyName}</div>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-4 py-3">
                <div>
                  <div className="text-sm font-semibold text-[#111D4E]">{t('deals.status')}</div>
                  <div className="text-xs text-slate-500">{t('deals.manageDealSubtitle')}</div>
                </div>
                <StatusBadge kind="deal" status={actionDealTarget.status} />
              </div>

              {NEXT_STATUS_OPTIONS[actionDealTarget.status]?.length ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('deals.status')}</label>
                    <select
                      value={statusSelections[actionDealTarget.id] || actionDealTarget.status}
                      onChange={(e) => setStatusSelections((prev) => ({ ...prev, [actionDealTarget.id]: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white"
                    >
                      {getSelectableStatusOptions(actionDealTarget.status).map((status) => (
                        <option key={status} value={status}>{t(`status.deal.${status}`)}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={() => updateDealStatus(actionDealTarget.id)}
                    disabled={
                      (statusSelections[actionDealTarget.id] || actionDealTarget.status) === actionDealTarget.status ||
                      statusBusy === `${actionDealTarget.id}:${statusSelections[actionDealTarget.id]}`
                    }
                    className="w-full px-4 py-2 bg-amber-100 text-amber-800 rounded-md text-sm font-semibold disabled:opacity-60"
                  >
                    {statusBusy === `${actionDealTarget.id}:${statusSelections[actionDealTarget.id]}` ? t('common.loading') : t('deals.updateStatus')}
                  </button>
                </div>
              ) : null}

              {canCloseDeal(actionDealTarget.status) && (
                <div className="space-y-2">
                  <div className="text-sm text-slate-600">{t('deals.closeHint')}</div>
                  <button
                    onClick={() => {
                      const selectedDeal = actionDealTarget;
                      setActionDealTarget(null);
                      openCloseDealModal(selectedDeal);
                    }}
                    className="w-full px-4 py-2 bg-[#47ABDB] text-white rounded-md font-semibold"
                  >
                    {t('deals.closeDeal')}
                  </button>
                </div>
              )}
            </div>
            <div className="px-6 pb-6 flex justify-end">
              <button
                type="button"
                onClick={() => setActionDealTarget(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {closeDealTarget && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center px-4 py-4">
          <form onSubmit={closeDeal} className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white text-gray-800 rounded-2xl shadow-sm border border-slate-200/80">
            <div className="px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
              <div className="text-sm font-semibold text-[#111D4E]">{t('deals.closeFormTitle')}</div>
              <div className="text-xs text-gray-600">{closeDealTarget.companyName}</div>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-sm">
                <span className="font-semibold text-[#111D4E]">{t('deals.setter')}:</span> {closeDealTarget.setterEmail || '-'}
              </div>
              <div className="text-sm">
                <span className="font-semibold text-[#111D4E]">{t('deals.closer')}:</span> {user?.email || '-'}
              </div>
              <div className="text-sm">
                <span className="font-semibold text-[#111D4E]">{t('deals.closerTier')}:</span> {user?.role === 'ADMIN' ? t('closerType.TEAM_MEMBER') : getCloserTierLabel(user?.closerType || 'TEAM_MEMBER')}
              </div>
              {ACTIVE_DEAL_STATUSES.includes(closeDealTarget.status as typeof ACTIVE_DEAL_STATUSES[number]) && (
                <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                  {t('deals.closeHint')}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">{t('deals.dealType')}</label>
                <select value={dealType} onChange={(e) => setDealType(e.target.value as typeof dealType)} className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white">
                  <option value="NEW_BUSINESS">{t('dealType.NEW_BUSINESS')}</option>
                  <option value="RENEWAL">{t('dealType.RENEWAL')}</option>
                  <option value="UPSELL">{t('dealType.UPSELL')}</option>
                  <option value="REACTIVATION">{t('dealType.REACTIVATION')}</option>
                  <option value="ORGANIZATIONAL">{t('dealType.ORGANIZATIONAL')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('deals.setterAssignment')}</label>
                <select value={setterMode} onChange={(e) => setSetterMode(e.target.value as typeof setterMode)} className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white">
                  <option value="EXISTING">{t('deals.setterAssignmentExisting')}</option>
                  <option value="CLOSER">{t('deals.setterAssignmentCloser')}</option>
                  <option value="NONE">{t('deals.setterAssignmentNone')}</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={originatedByCloser} onChange={(e) => setOriginatedByCloser(e.target.checked)} />
                <span>{t('deals.originatedByCloser')}</span>
              </label>
              <div>
                <label className="block text-sm font-medium mb-1">{t('deals.revenue')}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={revenue}
                  onChange={(e) => setRevenue(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md"
                  required
                />
              </div>
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setCloseDealTarget(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-[#47ABDB] disabled:opacity-60 text-white rounded-md font-semibold hover:bg-[#3d8fb8]"
                >
                  {loading ? t('common.loading') : t('deals.confirmClose')}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </section>
  );
};

export default DealManagement;
